import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { useEntities } from "@hooks/useEntities";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { MultiEntityAccountCombobox } from "@components/accounts/MultiEntityAccountCombobox";
import { findFirstResolvingAccount } from "@components/accounts/resolveAccountsForIds";
import {
    useWorkspaceAccountGroups,
    useWorkspaceAccounts,
} from "@api/account.api";
import {
    FormRuleActionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
} from "../../TransactionRuleForm/constants";
import { buildCashAccountActionValue } from "../../TransactionRuleForm/RuleActionsForm/accountActionValue";
import { TransferRuleFormSchema } from "../constants";

interface Props {
    transferDirection: TransferRuleDirection;
    index: number;
}

type MultiEntityComboboxProps = Pick<
    React.ComponentProps<typeof MultiEntityAccountCombobox>,
    | "identity"
    | "coverage"
    | "groupsByEntityId"
    | "groupsLoading"
    | "allowManuallyCreatedAcrossEntities"
    | "hideEntityIcons"
    | "selectedAccountIds"
>;

export const AccountActionForm: React.FC<Props> = ({
    transferDirection,
    index,
}) => {
    const entities = useEntities();
    const { values, setFieldValue } =
        useFormikContext<TransferRuleFormSchema>();
    const accounts = useWorkspaceAccounts();
    const accountGroups = useWorkspaceAccountGroups();

    const entityCondition = values.conditions[transferDirection].find(
        (condition) => condition.conditionType === FormRuleConditionType.ENTITY,
    );

    const conditionEntityIds = useMemo(() => {
        if (entityCondition?.entityValue?.length) {
            return entityCondition.entityValue;
        }
        return entities.filter((e) => !e.isPersonal).map((e) => e.id);
    }, [entityCondition, entities]);

    const isPersonalEntity = useMemo(
        () =>
            conditionEntityIds.some(
                (id) => entities.find((e) => e.id === id)?.isPersonal,
            ),
        [conditionEntityIds, entities],
    );

    const accountAction = values.actions[transferDirection]?.[index];

    const selectedAccountName = useMemo(
        () =>
            accountAction?.value?.accountName ??
            accounts?.data?.find(
                (account) => account.id === accountAction?.value?.accountId,
            )?.name ??
            findFirstResolvingAccount(
                accountAction?.value?.accountIds ?? [],
                accounts.data,
            )?.name,
        [accountAction, accounts],
    );

    const multiEntityComboboxProps: MultiEntityComboboxProps = {
        identity: "nameTypePath",
        coverage: "all",
        hideEntityIcons: true,
        groupsByEntityId: accountGroups.data,
        groupsLoading: accountGroups.isLoading,
        allowManuallyCreatedAcrossEntities: true,
        selectedAccountIds:
            accountAction?.value?.accountIds ??
            (accountAction?.value?.accountId
                ? [accountAction.value.accountId]
                : undefined),
    };

    if (isPersonalEntity) {
        return null;
    }

    return (
        <MultiEntityAccountCombobox
            selectedAccountName={selectedAccountName}
            onChange={(account) => {
                if (!account) {
                    void setFieldValue(
                        `actions.${transferDirection}.${index}.value`,
                        undefined,
                    );
                    return;
                }

                const updatedAction: Partial<TransactionRuleActionFormSchema> =
                    {
                        actionType: FormRuleActionType.ACCOUNT_ID,
                        value: buildCashAccountActionValue({ account }),
                        transferDirection,
                    };

                void setFieldValue(`actions.${transferDirection}.${index}`, {
                    ...accountAction,
                    ...updatedAction,
                });
            }}
            selectedEntityIds={conditionEntityIds}
            size="md"
            {...multiEntityComboboxProps}
        />
    );
};
